import React, { createContext, useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosClient from '../http/axios-client';

const TokenManagerContext = createContext({});

export const useFindUniqueCTX = (params) => {
  Object.keys(params)
    .forEach((key) => {
      if (params[key] === undefined || null === params[key]) {
        delete params[key];
      }
    });

  const urlParams = Object.keys(params).map((key) => {
    return `${ key }=${ params[key] }`;
  }).join('&');

  const {
    data: ctx,
    isLoading: ctxIsLoading,
  } = useQuery(
    [ 'CONTEXTS', urlParams ],
    () => {
      return axiosClient.get(`contexts/unique?${ urlParams }`);
    },

  );

  return {
    ctx,
    ctxIsLoading,
  };
};

export const useCTX = (ctxId) => {
  const {
    data: ctx,
    isLoading: ctxIsLoading,
  } = useQuery(
    [ 'CONTEXTS', ctxId ],
    () => {
      return axiosClient.get(`contexts/${ ctxId }`);
    },
    {
      enabled: !!ctxId,
    },
  );

  return {
    ctx,
    ctxIsLoading,
  };
};

export const useCategories = () => {
  const queryClient = useQueryClient();

  const { data: categories, isLoading: categoriesLoading } = useQuery(
    [ 'CATEGORIES' ],
    () => {
      return axiosClient.get('categories');
    },
  );

  const { mutateAsync: createCategory } = useMutation(
    [ 'CATEGORIES-CREATE' ],
    ({ name }) => {
      return axiosClient.post('categories', { name });
    },
    {
      onSettled() {
        return queryClient.invalidateQueries([ 'CATEGORIES' ]);
      },
    },
  );

  return {
    categories,
    categoriesLoading,
    createCategory,
  };
};

export const useTokens = () => {
  const queryClient = useQueryClient();

  const { data: tokens, isLoading: tokensLoading } = useQuery(
    [ 'TOKENS' ],
    () => {
      return axiosClient.get('tokens');
    },
  );

  const { mutateAsync: createToken } = useMutation(
    [ 'TOKENS-CREATE' ],
    ({ name }) => {
      return axiosClient.post('tokens', { name });
    },
    {
      onSettled() {
        return queryClient.invalidateQueries([ 'TOKENS' ]);
      },
    },
  );

  return {
    tokens,
    tokensLoading,
    createToken,
  };
};

export const useFindToken = (tokenName) => {
  const {
    data: token,
  } = useQuery(
    [ 'TOKEN', tokenName ],
    () => {
      return axiosClient.get(`tokens/?name=${ tokenName }&_limit=1`)
        .then((d) => {
          return d?.[0];
        });
    },
  );

  return {
    token,
  };
};

export const useContent = (ctxId, tokenId) => {
  const queryClient = useQueryClient();

  const { data: content, isLoading: contentIsLoading } = useQuery(
    [ 'CONTENT', { ctxId, tokenId }],
    () => {
      return axiosClient.get(`text-unions/?token=${ tokenId }&context=${ ctxId }`)
        .then((textUnions) => {
          if (!textUnions || !textUnions.length) {
            return undefined;
          }
          return textUnions[0].content;
        });
    },
    {
      enabled: !!ctxId && !!tokenId,
    },
  );

  const { mutateAsync: upsertContent } = useMutation(
    [ 'CONTENT-CREATE' ],
    ({ text }) => {
      return Promise.resolve()
        .then(() => {
          if (content && content.id) {
            return axiosClient.put(`contents/${ content.id }`, { text });
          }
          return axiosClient.post('contents', { text })
            .then((c) => {
              return axiosClient.post('text-unions', {
                context: ctxId,
                token: tokenId,
                content: c.id,
              });
            });
        });
    },
    {
      onSettled() {
        return queryClient.invalidateQueries([ 'CONTENT', { ctxId, tokenId }]);
      },
    },
  );

  return {
    content,
    contentIsLoading,
    upsertContent,
  };
};

export const TokenManagerProvider = (
  {
    children,
  },
) => {
  const ctx = {
  };

  return (
    <TokenManagerContext.Provider value={ ctx }>
      {children}
    </TokenManagerContext.Provider>
  );
};

export const useTokenManager = () => {
  const {
    contexts,
  } = useContext(TokenManagerContext);

  return {
    contexts,
  };
};
